import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { TableActionType } from '../../enums';
import { FileIconComponent, FileIconType } from '../knowledge-hub/file-icon/file-icon.component';

@Component({
  selector: 'educare-table',
  styleUrl: 'table.component.scss',
  templateUrl: 'table.component.html',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatIconModule,
    TranslateModule,
    CheckboxComponent,
    MatCheckboxModule,
    FileIconComponent
  ]
})
export class TableComponent implements OnInit {
  private readonly paginator = inject(MatPaginatorIntl);
  private readonly translate = inject(TranslateService);
  @Input() dataSource: any = {
    items: [],
    count: 0,
    page: 0,
    limit: 0
  };
  @Input() columns: any[] = [];
  // Consolidated selection configuration
  @Input() selectionConfig?: {
    enabled: boolean;
    selectedItems: any[];
    selectAll: boolean;
    selectionKey?: string;
    totalCount: number;
    selectedCount: number;
  };

  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() selectAllAcrossPages = new EventEmitter<boolean>();

  displayedColumns: string[] = [];
  TableActionType = TableActionType;

  get isIndeterminate(): boolean {
    if (!this.selectionConfig) return false;

    const totalCount = this.selectionConfig.totalCount;
    const selectedCount = this.selectionConfig.selectedCount;
    return selectedCount > 0 && selectedCount < totalCount;
  }

  get enableRowSelection(): boolean {
    return this.selectionConfig?.enabled || false;
  }

  get selectAll(): boolean {
    return this.selectionConfig?.selectAll || false;
  }

  get selectedItems(): any[] {
    return this.selectionConfig?.selectedItems || [];
  }

  get totalSelectedCount(): number {
    return this.selectionConfig?.selectedCount || 0;
  }

  ngOnInit() {
    this.displayedColumns = this.columns.map((col) => col.name);

    // Add checkbox column if row selection is enabled
    if (this.selectionConfig?.enabled) {
      this.displayedColumns.unshift('checkbox');
    }

    this.setPagniatorLabels();
  }

  handleAction({ $event, column, action, element }: any) {
    if (column?.onClick) {
      column.onClick($event, element, action.id);
    }
  }

  handleUsersClick({ $event, column, element }: any) {
    if (column?.onClick) {
      column.onClick($event, element);
    }
  }

  setPagniatorLabels() {
    this.paginator.itemsPerPageLabel = this.translate.instant('paginator.itemsPerPageLabel');
    this.paginator.nextPageLabel = this.translate.instant('paginator.nextPageLabel');
    this.paginator.previousPageLabel = this.translate.instant('paginator.previousPageLabel');
    this.paginator.firstPageLabel = this.translate.instant('paginator.firstPageLabel');
    this.paginator.lastPageLabel = this.translate.instant('paginator.lastPageLabel');
    const translatedLabel = this.translate.instant('paginator.getRangeLabel');
    this.paginator.getRangeLabel = (page: number, pageSize: number, length: number) => {
      length = Math.max(length, 0);
      const startIndex = page * pageSize;
      const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
      return translatedLabel
        .replace('{{startIndex}}', String(startIndex + 1))
        .replace('{{endIndex}}', String(endIndex))
        .replace('{{length}}', String(length));
    };
  }

  handlePageChange(pageEvent: PageEvent) {
    this.pageChange.emit(pageEvent);
  }

  handleSortChange(sort: Sort) {
    this.sortChange.emit(sort);
  }

  // Checkbox selection methods
  toggleSelectAll(): void {
    if (!this.selectionConfig) return;

    const newSelectAll = !this.selectionConfig.selectAll;
    this.selectAllAcrossPages.emit(newSelectAll);
  }

  toggleItemSelection(item: any): void {
    if (!this.selectionConfig) return;

    const selectionKey = this.selectionConfig.selectionKey || 'id';
    const itemKey = item[selectionKey];
    const currentSelection = [...this.selectionConfig.selectedItems];
    const index = currentSelection.indexOf(itemKey);

    if (index > -1) {
      currentSelection.splice(index, 1);
    } else {
      currentSelection.push(itemKey);
    }

    this.selectionChange.emit(currentSelection);
  }

  isItemSelected(item: any): boolean {
    if (!this.selectionConfig) return false;

    const selectionKey = this.selectionConfig.selectionKey || 'id';
    return this.selectionConfig.selectedItems.includes(item[selectionKey]);
  }

  getFileIconType(element: any): FileIconType {
    if (element.type === 'folder') {
      return 'folder';
    }

    const fileName = element.name?.toLowerCase() || '';

    if (fileName.includes('.pdf')) {
      return 'pdf';
    }

    if (fileName.includes('.xls')) {
      return 'xls';
    }

    if (fileName.includes('.xlsx')) {
      return 'xlsx';
    }

    if (fileName.includes('.doc')) {
      return 'doc';
    }

    if (fileName.includes('.docx')) {
      return 'docx';
    }

    if (fileName.includes('.csv')) {
      return 'csv';
    }

    return 'doc';
  }
}
